import { LogClient } from '../clients/LogClient';
import { Settings } from '../common/Settings';
import { Logger } from '@shared/common';
import type { IRecordLogDTO, IRecordLogger } from '@shared/common';

/** File-local constants for RecordLoggerFrontend. */
const localConstants = {
  logContext: 'RecordLoggerFrontend',
  /** Maximum entries buffered before a forced flush is triggered. */
  bufferMax: 1000,
  /** Auto-flush poll interval in ms. */
  flushIntervalMs: 5000,
  /** Buffer size at which an opportunistic flush is triggered (independent of timer). */
  flushSoftLimit: 50,
  /** Synthetic origin used when the buffer overflows. */
  overflowOrigin: 'RecordLoggerFrontend',
  /** Synthetic message used when the buffer overflows. */
  overflowMessage: 'log buffer overflow — oldest entries dropped',
  /** LocalStorage key for the cached anonymous device ID. */
  deviceIDKey: 'va.recordLogger.deviceID',
  /** Metadata key under which `window.location.href` is recorded. */
  metadataURLKey: 'url',
  /** Metadata key under which `navigator.userAgent` is recorded. */
  metadataUAKey: 'userAgent',
  /** Metadata key under which the device ID is recorded. */
  metadataDeviceIDKey: 'deviceID',
  /** `document.visibilityState` value indicating the page is no longer visible — used to trigger an eviction-safe flush. */
  visibilityHidden: 'hidden'
} as const;

/**
 * Frontend implementation of {@link IRecordLogger}. Buffers log/warn/error entries and POSTs them in batches to `/api/lobby/log` via {@link LogClient}.
 *
 * - Buffer cap: {@link localConstants.bufferMax}. Past this we drop the OLDEST entries and append a synthetic overflow record so the loss is visible in the recorded stream.
 * - Flush triggers: timer ({@link localConstants.flushIntervalMs}), soft threshold ({@link localConstants.flushSoftLimit}), and `beforeunload` (best-effort via `navigator.sendBeacon` fallback).
 * - Re-entry: {@link import('../../../common/src/internal/ConsoleLogger').ConsoleLogger} already guards against recursive `Logger.Error` from inside `Record`. This class adds no additional guard.
 * - Stamps each entry with `version`, `environment`, `metadata.url`, `metadata.userAgent`, and `metadata.deviceID` so the server-side row identifies the producing client.
 */
export class RecordLoggerFrontend implements IRecordLogger {
  /** In-memory buffer of pending log entries. */
  private buffer: IRecordLogDTO[] = [];

  /** Active in-flight flush promise (single-flight). */
  private inflight: Promise<void> | null = null;

  /** Periodic flush timer handle. */
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  /** Cached anonymous device ID, lazily generated and persisted to localStorage. */
  private deviceID: string;

  /** @inheritdoc */
  constructor() {
    this.deviceID = this.resolveDeviceID();
    if (typeof window !== 'undefined') {
      // No matching destroy()/teardown is provided by design: this is a process-lifetime singleton created once at app boot (one per page load) and never re-instantiated. The flush timer and the
      // three unload listeners must live for the entire page lifetime so the final batch is sent on close; the browser reclaims the timer and listeners when it discards the document on navigation or
      // tab close, so there is nothing to clean up. The only scenario a destroy() would help is dev hot-reload churn, which does not justify shipping otherwise-dead teardown code.
      this.flushTimer = setInterval(() => void this.Flush(), localConstants.flushIntervalMs);
      // `beforeunload` fires for desktop tab close / navigation but is unreliable on mobile and bfcache transitions.
      // `pagehide` fires for those cases (incl. iOS tab swipe-away and OS-driven memory eviction).
      // `visibilitychange` (state=hidden) catches app-switch/lock-screen on mobile before the OS evicts the tab.
      // All three call `flushOnUnload` which uses `navigator.sendBeacon` so the POST survives teardown.
      window.addEventListener('beforeunload', () => this.flushOnUnload());
      window.addEventListener('pagehide', () => this.flushOnUnload());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === localConstants.visibilityHidden) this.flushOnUnload();
      });
    }
  }

  /** @inheritdoc */
  Record(entry: IRecordLogDTO): void {
    const decorated: IRecordLogDTO = {
      ...entry,
      version: entry.version ?? Settings.VERSION,
      environment: entry.environment ?? Settings.ENV_MODE,
      metadata: {
        ...(entry.metadata ?? {}),
        [localConstants.metadataURLKey]: typeof window !== 'undefined' ? window.location.href : '',
        [localConstants.metadataUAKey]: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        [localConstants.metadataDeviceIDKey]: this.deviceID
      }
    };

    this.buffer.push(decorated);

    // Drop-oldest with a synthetic overflow marker so the gap is visible server-side. The marker takes the place of one dropped entry rather than being added on top: the post-trim buffer must be
    // exactly `bufferMax`, because `Flush` posts the whole buffer in one request and the server rejects a batch larger than that - which would discard the very batch the marker documents.
    if (this.buffer.length > localConstants.bufferMax) {
      const overflow = this.buffer.length - localConstants.bufferMax;
      this.buffer.splice(0, overflow + 1);
      this.buffer.unshift({
        category: 'warning',
        origin: localConstants.overflowOrigin,
        text: `${localConstants.overflowMessage} (count=${overflow + 1})`,
        version: Settings.VERSION,
        environment: Settings.ENV_MODE
      });
    }

    if (this.buffer.length >= localConstants.flushSoftLimit) {
      void this.Flush();
    }
  }

  /** @inheritdoc */
  async Flush(): Promise<void> {
    if (this.inflight) return this.inflight;
    if (this.buffer.length === 0) return;

    const batch = this.buffer.splice(0, this.buffer.length);
    this.inflight = (async () => {
      try {
        Logger.Debug(localConstants.logContext, `flushing ${batch.length} ${batch.length === 1 ? 'entry' : 'entries'} to /log`);
        await LogClient.GetInstance().PostBatch(batch);
      } catch (err: unknown) {
        // Console-only — a failed POST must not call `Logger.Error` (would re-enter the recorder). Drop the batch to avoid unbounded retry growth.
        Logger.ErrorLocal(localConstants.logContext, `flush failed, dropping ${batch.length} entries: ${Logger.GetErrorMessage(err)}`);
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }

  /**
   * Best-effort synchronous flush during page-eviction events (`beforeunload`, `pagehide`, `visibilitychange`=hidden). Uses `navigator.sendBeacon` to hand the batch to the browser as a
   * background POST that survives navigation, tab-close, OS app-switch, and bfcache transitions.
   */
  private flushOnUnload(): void {
    if (this.buffer.length === 0) return;
    const batch = this.buffer.splice(0, this.buffer.length);
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    try {
      const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
      navigator.sendBeacon(`${Settings.SERVICE_LOBBY_URI}/log`, blob);
    } catch (err: unknown) {
      // Console-only — page is unloading and the recorder pipeline is shutting down.
      Logger.WarnLocal(localConstants.logContext, `sendBeacon failed: ${Logger.GetErrorMessage(err)}`);
    }
  }

  /**
   * Resolves and caches an anonymous device ID in `localStorage` for cross-session metadata correlation. Falls back to an in-memory random ID when storage is unavailable.
   *
   * @returns The device ID string.
   */
  private resolveDeviceID(): string {
    try {
      if (typeof localStorage === 'undefined') return this.randomID();
      const existing = localStorage.getItem(localConstants.deviceIDKey);
      if (existing) return existing;
      const generated = this.randomID();
      localStorage.setItem(localConstants.deviceIDKey, generated);
      return generated;
    } catch {
      return this.randomID();
    }
  }

  /** @returns A random ID string suitable for an anonymous device identifier. */
  private randomID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
